import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen, Gamepad2, Users, CircleDollarSign, Server, Wrench, HelpCircle, ArrowRight, MapPin, Spade, Cpu, Vault, Landmark, ShieldHalf, Search, CornerDownRight } from "lucide-react";
import { Link } from "react-router-dom";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import Seo from "@/components/Seo";
import { WIKI_SECTIONS, renderWikiBody, searchWiki } from "@/components/menu/wikiContent";

const ICONS = { gamepad: Gamepad2, users: Users, dollar: CircleDollarSign, server: Server, wrench: Wrench, help: HelpCircle, map: MapPin, spade: Spade, cpu: Cpu, vault: Vault, landmark: Landmark, shield: ShieldHalf };

export default function WikiPage() {
  const [active, setActive] = useState("getting-started");
  const [query, setQuery] = useState("");
  const section = WIKI_SECTIONS.find((s) => s.id === active);
  const searching = query.trim().length >= 2;
  const results = searching ? searchWiki(query) : [];

  return (
    <div data-testid="wiki-page" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col">
      <Seo title="New Citizen Wiki — Official Knowledge Base" description="Guides for players, server owners and modders — from your first shift to running a community." />
      <SiteNav />

      <header className="border-b border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 grid-texture opacity-40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="font-mono text-xs tracking-[0.4em] text-nc-blue uppercase mb-3 flex items-center gap-2"><BookOpen className="w-4 h-4" /> New Citizen // Official Wiki</p>
            <h1 className="font-sans font-bold uppercase tracking-tight text-3xl sm:text-5xl text-zinc-50">Know the city.</h1>
            <p className="font-mono text-sm text-zinc-400 mt-4 max-w-2xl leading-relaxed">The official knowledge base — from your first shift to running your own server and building mods for it.</p>
            <div className="relative max-w-md mt-6">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
              <input
                data-testid="wiki-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search the knowledge base…"
                className="w-full bg-black/60 border border-white/10 focus:border-nc-orange/60 pl-10 pr-4 py-3 font-mono text-xs text-zinc-100 placeholder:text-zinc-600 focus:outline-none transition-colors"
              />
            </div>
          </motion.div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10 w-full flex-1 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">
        <aside className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible lg:sticky lg:top-24 self-start w-full">
          {searching ? (
            results.length === 0 ? (
              <p data-testid="wiki-search-empty" className="font-mono text-[10px] text-zinc-600 uppercase tracking-[0.15em] px-4 py-3">No hits for "{query.trim()}"</p>
            ) : (
              results.map((r) => {
                const Icon = ICONS[r.icon] || Gamepad2;
                return (
                  <button
                    key={r.id}
                    data-testid={`wiki-search-result-${r.id}`}
                    onClick={() => { setActive(r.id); setQuery(""); }}
                    className="text-left px-4 py-3 border-l-2 border-white/10 hover:border-nc-orange/60 text-zinc-400 hover:text-zinc-100 transition-all w-full"
                  >
                    <span className="font-mono text-[10px] tracking-[0.15em] uppercase flex items-center gap-2">
                      <Icon className="w-3.5 h-3.5 shrink-0 text-nc-orange" /> {r.label}
                      <CornerDownRight className="w-3 h-3 text-zinc-600" />
                    </span>
                    <span className="font-mono text-[9px] text-zinc-500 leading-relaxed block mt-1 line-clamp-2">{r.snippet}</span>
                  </button>
                );
              })
            )
          ) : (
            WIKI_SECTIONS.map((s) => {
              const Icon = ICONS[s.icon] || Gamepad2;
              return (
                <button
                  key={s.id}
                  data-testid={`wiki-nav-${s.id}`}
                  onClick={() => setActive(s.id)}
                  className={`text-left font-mono text-[10px] tracking-[0.15em] uppercase px-4 py-3 border-l-2 transition-all duration-200 whitespace-nowrap flex items-center gap-2.5 ${active === s.id ? "border-nc-orange text-zinc-50 bg-white/[0.04]" : "border-white/10 text-zinc-500 hover:text-zinc-200 hover:border-white/30"}`}
                >
                  <Icon className="w-3.5 h-3.5 shrink-0" /> {s.label}
                </button>
              );
            })
          )}
          <Link to="/new-citizen/servers" className="text-left font-mono text-[10px] tracking-[0.15em] uppercase px-4 py-3 border-l-2 border-transparent text-nc-blue hover:text-zinc-50 transition-colors flex items-center gap-2.5 mt-2">
            Server hub <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </aside>

        <div className="min-w-0">
          <AnimatePresence mode="wait">
            <motion.div key={active} data-testid="wiki-content" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }} className="border border-white/10 bg-[#070707] p-6 sm:p-8">
              <p className="font-sans font-bold uppercase text-xl text-zinc-50 mb-6 pb-4 border-b border-white/10">{section.label}</p>
              {renderWikiBody(section.body)}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
